import {
  AccountsOrderBy,
  type MentionAccountSearchQueryVariables,
} from "@/graphql/codegen/graphql";
import type { PartialAccount } from "@/utils/types";

export const MENTION_SEARCH_LIMIT = 10;

const MENTION_USERNAME_REGEX = /^[A-Za-z0-9_]{1,26}$/;

export const normalizeMentionSearchQuery = (query: string) => {
  const normalized = query.trim();

  if (!MENTION_USERNAME_REGEX.test(normalized)) {
    return null;
  }

  return normalized;
};

export const createMentionAccountSearchVariables = (
  query: string,
): MentionAccountSearchQueryVariables | null => {
  const normalized = normalizeMentionSearchQuery(query);

  if (!normalized) {
    return null;
  }

  return {
    accountsFilter: {
      isBlacklisted: {
        equalTo: false,
      },
      username: {
        startsWithInsensitive: normalized,
      },
    },
    first: MENTION_SEARCH_LIMIT,
    orderBy: [AccountsOrderBy.TotalBalanceUsdCentsDesc],
  };
};

export const getMentionSearchCacheKey = (query: string) => query.toLowerCase();

export const normalizeMentionSearchAccounts = (
  accounts: Array<PartialAccount | null | undefined>,
) => {
  const seen = new Set<string>();
  const normalizedAccounts: PartialAccount[] = [];

  for (const account of accounts) {
    if (!account?.publicKey || !account.username) {
      continue;
    }

    if (seen.has(account.publicKey)) {
      continue;
    }

    seen.add(account.publicKey);
    normalizedAccounts.push({
      publicKey: account.publicKey,
      totalBalanceUsdCents: account.totalBalanceUsdCents,
      username: account.username,
    });
  }

  return normalizedAccounts;
};

export const getCachedMentionSearchAccounts = (
  cache: ReadonlyMap<string, PartialAccount[]>,
  query: string,
) => {
  const normalized = normalizeMentionSearchQuery(query);

  if (!normalized) {
    return [];
  }

  const cacheKey = getMentionSearchCacheKey(normalized);
  const exactCachedAccounts = cache.get(cacheKey);

  if (exactCachedAccounts) {
    return exactCachedAccounts;
  }

  const prefixKey = [...cache.keys()]
    .filter((key) => cacheKey.startsWith(key))
    .sort((left, right) => right.length - left.length)[0];

  if (!prefixKey) {
    return [];
  }

  return (cache.get(prefixKey) ?? [])
    .filter((account) => account.username?.toLowerCase().startsWith(cacheKey))
    .slice(0, MENTION_SEARCH_LIMIT);
};
